<template>
    <div
        class="chat-conversation p-3 p-lg-4"
        :class="{ 'has-wallpaper-image': hasWallpaperImage }"
        :style="{ '--chat-wallpaper-image': wallpaperImageVar }"
        ref="chatConversation"
        @scroll.passive="handleScroll"
        @wheel.passive="liberarAncora"
        @touchstart.passive="liberarAncora"
        @mousedown="liberarAncora"
        @keydown="liberarAncora"
    >

        <div v-if="isLoadingMessages" class="position-absolute top-50 start-50 translate-middle z-3">
            <div class="spinner-border shadow-sm bg-body" 
                 style="background: transparent !important; color: var(--bs-success); width: 2.5rem; height: 2.5rem; border-width: 0.25em; border-radius: 50%;"
                 role="status">
                <span class="visually-hidden">Carregando...</span>
            </div>
        </div>

        <!-- Invólucro estável de tudo que rola: é ele que o ResizeObserver observa.
             Qualquer mudança de altura do conteúdo (mensagem nova, mídia terminando
             de carregar, página antiga, balão de "escrevendo...") passa por aqui. -->
        <div ref="conteudoConversa">
        <template v-if="!isLoadingMessages">
            <div 
                v-for="group in groupedMessages" 
                :key="group.label" 
                class="day-section"
            >
                <div class="chat-day-title-container">
                    <div class="chat-day-title">
                        <span class="title">{{ group.label }}</span>
                    </div>
                </div>

                <ul class="list-unstyled mb-0">
                    
                    <template v-for="msg in group.msgs" :key="msg.id">

                        <li v-if="msg.id === itemDivisorAbertura || msg.id === itemDivisorNovas"
                            class="unread-divider-container" role="separator"
                            :data-divisor-abertura="msg.id === itemDivisorAbertura ? '' : undefined">
                            <div class="unread-divider-line"></div>
                            <div class="unread-divider-badge">
                                {{ rotuloNaoLidas(msg.id === itemDivisorAbertura ? initialUnreadCount : newMessagesCount) }}
                            </div>
                        </li>
                        <ChatInternalNote
                            v-if="msg.type === 'internal-note'"
                            :message="msg"
                            :data-message-id="msg.id"
                        />
                        <ChatMessage
                            v-else
                            :message="msg"
                            :highlight-query="highlightQuery"
                            :data-message-id="msg.id" 
                            :is-forwarding="isForwarding"
                            :forwarding-message-ids="forwardingMessageIds"
                            :pinned-message-id="pinnedMessageId" 
                            @delete="handleDelete"
                            @edit="handleEdit"
                            @reply="handleReply"
                            @react="handleReact"
                            @forward="handleForward"
                            @copy="handleCopy"
                            @star="handleStar"
                            @pin="handlePin"
                            @toggle-forward="handleToggleForward"
                        />
                    </template>
                </ul>
            </div>
        </template>
        <!-- Balão de "escrevendo...". Fica FORA do v-if de carregamento e depois
             do último grupo, para ser sempre o último nó do fluxo da conversa. -->
        <div v-if="presencaDigitando && !isLoadingMessages" class="presenca-bubble-row"
             :class="{ 'presenca-em-grupo': chatStore.isGroupChat }">
            <div class="presenca-bubble" role="status" aria-live="polite"
                 :aria-label="presencaDigitando.texto">
                <span v-if="presencaDigitando.autor" class="presenca-autor"
                      :style="presencaDigitando.cor ? { color: presencaDigitando.cor } : undefined">
                    {{ presencaDigitando.autor }}
                </span>
                <!-- Gravando áudio tem vocabulário próprio: mic vermelho + barras.
                     Sem isso o balão era idêntico ao de digitação — a diferença só
                     existia dentro do `texto`, que aqui nunca é renderizado. -->
                <template v-if="presencaDigitando.tipo === 'recording'">
                    <i class="ri-mic-fill presenca-mic" aria-hidden="true"></i>
                    <span class="presenca-waves" aria-hidden="true">
                        <i></i><i></i><i></i><i></i>
                    </span>
                </template>
                <span v-else class="presenca-dots" aria-hidden="true">
                    <i></i><i></i><i></i>
                </span>
            </div>
        </div>
        </div>

        <transition name="fade">
            <button v-show="showScrollBottomBtn || newMessagesCount > 0"
                class="btn btn-scroll-bottom shadow-lg"
                :class="{ 
                    'sidebar-open': isSidebarOpen,
                    'has-new-messages': newMessagesCount > 0 
                }" 
                @click="handleScrollToBottomClick" 
                title="Ir para o final">
                
                <span v-if="newMessagesCount > 0" class="scroll-badge">
                    {{ newMessagesCount }}
                </span>

                <i class="ri-arrow-down-double-line"></i>
            </button>
        </transition>
    </div>
</template>

<script lang="ts">
import { Message } from '@/mocks/app/mockChatData';
import { branding } from '@/config/branding';
import ChatMessage from './ChatMessage.vue';
import ChatInternalNote from './ChatInternalNote.vue';
import { rotuloPresenca, useChatStore } from '@/stores/app/chatStore';
import { rotuloPresencaChatInterno } from '@/stores/app/chatInternoStore';
import { useUiStore } from '@/stores/app/uiStore';
import { computed, defineComponent, nextTick, PropType, ref, shallowRef, watch } from 'vue';
import { throttle } from '@/utils/throttle';
import { useInternalChatStore } from '../../../../stores/app/chatInternoStore';
import { delay } from '@/utils/delay';
import { waitUntil } from '@/utils/waitUntil';
import { debounce } from '@/utils/debounce';
import { idsNaoLidas, itemComPrimeiraNaoLida, rotuloNaoLidas } from '@/utils/mensagensNaoLidas';

/**
 * Onde o separador de não lidas fica ao abrir: abaixo do chip de data fixo
 * (sticky, ~10–45px do topo), deixando uma fresta da última mensagem lida.
 */
const MARGEM_TOPO_DIVISOR = 72;

/**
 * Primeira mensagem visível e a distância dela até o topo da conversa. Pelo id, não
 * pelo elemento: página antiga que traz outro dia desloca o índice dos grupos
 * (`:key="group.label + index"`) e o Vue recria todos os nós de mensagem.
 */
type ReferenciaVisivel = { id: string; topo: number };

/**
 * Chrome/Edge/Firefox seguram sozinhos a posição de quem lê quando algo ACIMA
 * muda de altura (`overflow-anchor`). O Safari — e com ele o PWA no iOS — não.
 */
const ancoragemNativa = typeof CSS !== 'undefined' && CSS.supports('overflow-anchor', 'auto');

export default defineComponent({
    name: 'ChatConversation',
    components: {
        ChatMessage,
        ChatInternalNote
    },
    setup(props) {
        const chatStore = useChatStore();
        const uiStore = useUiStore();
        const internalChatStore = useInternalChatStore();
        const currentStore = computed(() => internalChatStore.isInternalChatMode ? internalChatStore : chatStore);
        const resizeObserver = ref<ResizeObserver | null>(null);
        const chatContainerRef = ref<HTMLDivElement | null>(null); 
        
        const newMessagesCount = ref(0);
        const tempUnreadMessageId = ref<string | null>(null);
        
        const isLoadingMessages = computed(() => currentStore.value.isLoadingMessages);
        
        const isUserAtBottom = ref(true);
        const showScrollBottomBtn = ref(false);

        /**
         * Mensagens que estavam não lidas quando a conversa abriu. Congelado na
         * abertura de propósito: calculado ao vivo ("as N últimas"), o separador
         * descia uma posição a cada mensagem nova que chegava.
         */
        const idsNaoLidasNaAbertura = shallowRef<ReadonlySet<string>>(new Set());

        /**
         * A conversa ativa começou a carregar e ainda não foi posicionada. Só existe
         * com `exibirNaoLidas`: os viewers não acompanham a conversa ativa da store.
         */
        const aberturaPendente = ref(props.exibirNaoLidas);

        /**
         * Quem manda na posição até o primeiro gesto do usuário (dali em diante ela é dele):
         *  - 'divisor': abriu com não lidas. Toda mudança de altura do conteúdo (mídia
         *    carregando acima, página antiga, composer crescendo) re-alinha o separador;
         *  - 'fim': seguindo a última mensagem (abertura animada, mensagem nova). Mudança
         *    de altura redireciona o deslize para o novo fim, sem cortar a animação.
         */
        let ancora: 'divisor' | 'fim' | null = null;
        let ultimoScrollTop = 0;

        let referenciaLeitura: ReferenciaVisivel | null = null;

        // Troca de conversa (ou de modo: cliente ⇄ chat interno) zera a leitura da anterior.
        watch(() => [internalChatStore.isInternalChatMode, currentStore.value.activeChatId], () => {
            newMessagesCount.value = 0;
            tempUnreadMessageId.value = null;
            idsNaoLidasNaAbertura.value = new Set();
            ancora = null;
            aberturaPendente.value = props.exibirNaoLidas;
        });

        // O chat interno recarrega a MESMA conversa quando ela é selecionada de novo
        // (`setActiveChat` zera `messages` e busca tudo), sem trocar o id. Carga que
        // começa do zero é abertura; a da paginação — que lá também liga
        // `isLoadingMessages` — começa com mensagens na tela.
        watch(isLoadingMessages, (carregando) => {
            if (carregando && currentStore.value.messages.length === 0) aberturaPendente.value = props.exibirNaoLidas;
        });

        // Mensagem nova = a ÚLTIMA mudou e a lista cresceu. Só o tamanho não basta: a
        // página antiga entra pelo começo e também aumenta a lista — era contada como
        // nova, acendia o badge e punha um separador falso antes da última mensagem.
        watch(() => [props.messages.length, props.messages.at(-1)?.id] as const, async ([newLen, ultimoId], [oldLen, ultimoIdAnterior]) => {
            if (newLen <= oldLen || ultimoId === ultimoIdAnterior) return;
            // A carga da abertura também troca a última; quem posiciona ali é
            // `posicionarAbertura`. Tratada aqui, rolava para o fim e marcava como
            // lida até a prévia de atendimento, que o `selectChat` deixa não lida.
            if (isLoadingMessages.value || aberturaPendente.value) return;

            const lastMsg = props.messages[newLen - 1];

            if (lastMsg.isSent) {
                ancora = 'fim';
                newMessagesCount.value = 0;
                tempUnreadMessageId.value = null;
                await nextTick();
                scrollToBottomSmoothLocal();
                return;
            }

            const el = chatConversation.value; 
            // Verifica se está no fim da página (tolerância de 200px). No meio da animação
            // de abertura ainda não está, mas está indo para lá.
            const isReallyAtBottom = ancora === 'fim' || (el ? (el.scrollHeight - el.clientHeight - el.scrollTop < 200) : true);

            if (isReallyAtBottom) {
                // Quem já está no fim acompanha a conversa, mesmo com o separador na tela.
                ancora = 'fim';
                await nextTick();
                scrollToBottomSmoothLocal();
                newMessagesCount.value = 0; 
                
                // Se o usuário já está lá embaixo vendo tudo, marca como lido no store.
                // Só na conversa ativa: um viewer carregando o histórico não é leitura dela.
                if (props.exibirNaoLidas && chatStore.activeChatId) {
                    chatStore.resolveMarkChatAsRead(chatStore.activeChatId);
                }
            } 
            else {
                newMessagesCount.value++;
                if (!tempUnreadMessageId.value) {
                    tempUnreadMessageId.value = lastMsg.id;
                }
                // AQUI NÃO MARCADO COMO LIDO, permitindo que o badge do ChatItem apareça
            }
        });

        const scrollToBottomSmoothLocal = () => {
            const el = chatConversation.value;
            if (el) {
                el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
            }
        };

        // Balão de "escrevendo..." da conversa aberta. Chat interno tem presença
        // própria (CHAT_INTERNO.STATUS_USUARIO), então não entra aqui.
        const presencaDigitando = computed(() => {
            if (internalChatStore.isInternalChatMode) {
                // Chat interno é 1:1 e não grava áudio: sempre `composing`, sem autor
                // e sem cor. O balão é o mesmo — só a fonte do sinal muda (socket
                // direto, em vez de NATS via whiz-server).
                const texto = rotuloPresencaChatInterno(internalChatStore.activeChatId);
                return texto ? { texto, cor: null, autor: null, tipo: 'composing' as const } : null;
            }
            return rotuloPresenca(chatStore.activeChat, chatStore.contatosByCodigo);
        });

        // O balão de "escrevendo..." (e a troca `composing → recording`, uns 8px mais
        // alta) não precisa de watch próprio: ele vive no invólucro observado, e o
        // ResizeObserver cola no fim quem já estava no fim — ninguém lendo o
        // histórico é arrastado para baixo por causa de uma presença.

        const isVisualMedia = (m: Message) => {
            if (m.type !== 'file' && m.type !== 'imagem') return false;
            if (m.uploadError || m.readStatus === 'error') return false; // ← não agrupa mensagens com erro
            // Mídia ainda no reparo do servidor (sem url): fica no próprio balão, com
            // miniatura e "Carregando mídia…". Na grade viraria um <video>/<img> sem
            // src. Quando `MENSAGEM.MIDIA` traz a url, ela volta a agrupar.
            if (m.midiaPendente || !m.fileData?.url) return false;
            const mime = m.fileData?.mimeType || '';
            return mime.startsWith('image/') || mime.startsWith('video/');
        };

        const groupConsecutiveMedia = (messages: Message[]): Message[] => {
            const result: Message[] = [];
            let buffer: Message[] = [];

            const flushBuffer = () => {
                if (buffer.length === 0) return;
                
                if (buffer.length >= 4) {
                    const lastMsg = buffer[buffer.length - 1];
                    const firstMsg = buffer[0];
                    
                    const gridMsg = {
                        ...lastMsg,
                        id: `grid-${firstMsg.id}`,
                        type: 'media_grid',
                        text: buffer.length + ' arquivos de mídia',
                        gridMessages: [...buffer],
                        agentName: buffer.find(m => m.agentName)?.agentName ?? undefined
                    } as any;
                    
                    result.push(gridMsg);
                } else {
                    result.push(...buffer);
                }
                buffer = [];
            };

            for (const msg of messages) {
                if (isVisualMedia(msg)) {
                    if (msg.fileData?.caption) {
                        flushBuffer();
                        result.push(msg);
                    } else if (buffer.length === 0) {
                        buffer.push(msg);
                    } else {
                        const prev = buffer[0];
                        const sameSender = (prev.isSent === msg.isSent) && (prev.sender === msg.sender);
                        
                        if (sameSender) {
                            buffer.push(msg);
                        } else {
                            flushBuffer();
                            buffer.push(msg);
                        }
                    }
                } else {
                    flushBuffer();
                    result.push(msg);
                }
            }
            flushBuffer(); 
            return result;
        };

        // Função auxiliar para formatar a data (Label)
        const getDateLabel = (dateString: string) => {
            const thisMessageDate = new Date(dateString);
            const todayDate       = new Date();
            const yesterdayDate   = new Date(Date.now() - (24 * 3600 * 1000));
            const aWeekAgo        = new Date(Date.now() - (7 * 24 * 3600 * 1000));

            const isSameDay = (d1: Date, d2: Date) => d1.toDateString() === d2.toDateString();
            const capitalize1st = (input: string) => input.charAt(0).toUpperCase() + input.slice(1);

            if (isSameDay(thisMessageDate, todayDate)) return 'Hoje';
            if (isSameDay(thisMessageDate, yesterdayDate)) return 'Ontem';
            
            if (thisMessageDate > aWeekAgo) {
                return capitalize1st(thisMessageDate.toLocaleString('pt-BR', { weekday: 'long' }));
            }
            
            const day = thisMessageDate.getDate();
            const month = thisMessageDate.toLocaleString('pt-BR', { month: 'long' });
            
            if (thisMessageDate.getFullYear() === todayDate.getFullYear()) {
                return `${day} de ${month}`;
            }
            
            return `${day} de ${month} de ${thisMessageDate.getFullYear()}`;
        };

        /**
         * Mensagem GENERICA que não tem NADA para desenhar. O filtro existe para
         * não poluir a conversa com bolhas vazias do que o servidor não soube
         * tipar, e era casado só pelo texto '❔ Genérica'.
         *
         * Isso engolia conteúdo legítimo: publicação do Instagram compartilhada
         * (post/reel) é persistida como GENERICA com `compartilhamento`, e a
         * mensagem sumia da conversa inteira — nem bolha vazia aparecia, o que
         * fazia parecer que o webhook não tinha chegado.
         *
         * O texto continua sendo o gatilho (é o que marca a genérica de verdade),
         * mas qualquer carga que a bolha saiba desenhar salva a mensagem.
         */
        const ehGenericaSemConteudo = (msg: Message) =>
            !!msg.text?.includes("❔ Genérica") && !msg.compartilhamento && !msg.catalogOrderData;

        const nonGenericMessages = computed(() => {
            if (props.messages.length == 0) return [];
            const msgs = props.messages;
            return msgs.filter(msg => !ehGenericaSemConteudo(msg))
        });

        // Altera o fluxo para casos de mensagens "apenas assinatura" dando match em um regex específico
        const onlySignatureRegex = /^\*([^:\n]+):\*\s?$/;
        /** Janela em que a assinatura automática e sua mídia são consideradas do mesmo envio */
        const AUTO_SIGNATURE_WINDOW_MS = 60 * 1000;

        const onlySignatureSquashedMessages = computed(() => {
            const messageIsSignatureOnly = (msg: Message) => msg.type == 'text' && onlySignatureRegex.test(msg.text ?? '');
            if (nonGenericMessages.value.length == 0) return [];
            const msgs = nonGenericMessages.value;

            // A assinatura automática só vira mensagem separada quando o envio é AUDIO/FIGURINHA/
            // DOCUMENTO/IMAGEM/VIDEO (ver handleSendSignatureMessage no chatStore) — nunca antes de
            // um texto. Ela aparece *antes* da mídia ao recarregar o histórico (ordenado por data) e
            // *depois* dela em tempo real (o balão otimista da mídia entra na lista primeiro), então
            // os dois vizinhos precisam ser considerados.
            const isSameSendAsMedia = (signatureMsg: Message, neighbor: Message|undefined) => {
                if (!neighbor || !neighbor.isSent || neighbor.type == 'text') return false;
                // O balão otimista da mídia ainda não tem idUsuarioSender, então só compara quando ambos existem
                const bothSendersKnown = !!neighbor.idUsuarioSender && !!signatureMsg.idUsuarioSender;
                if (bothSendersKnown && neighbor.idUsuarioSender != signatureMsg.idUsuarioSender) return false;
                const delta = Math.abs(new Date(neighbor.time).getTime() - new Date(signatureMsg.time).getTime());
                return delta <= AUTO_SIGNATURE_WINDOW_MS;
            };

            // Primeiro, ao encontrar essas mensagens, colocar o dado de assinatura na mídia do mesmo envio
            const absorbedIndexes = new Set<number>();
            for (let i = 0; i < msgs.length; i++) {
                const msg = msgs[i];
                if (!messageIsSignatureOnly(msg)) continue;

                const mediaOfSameSend = [msgs.at(i + 1), i > 0 ? msgs.at(i - 1) : undefined]
                    .find(neighbor => isSameSendAsMedia(msg, neighbor));

                // Sem mídia por perto a assinatura foi digitada à mão pelo operador: mantém na tela
                if (!mediaOfSameSend) continue;

                if (!mediaOfSameSend.agentName) mediaOfSameSend.agentName = onlySignatureRegex.exec(msg.text ?? '')?.at(1);
                absorbedIndexes.add(i);
            }
            // Depois, remover apenas as assinaturas que foram absorvidas pela mídia
            return msgs.filter((_, i) => !absorbedIndexes.has(i));
        });

        /**
         * Anotações internas projetadas como itens de timeline.
         *
         * Elas NÃO vivem em `chatStore.messages` de propósito: aquele array é a
         * fonte de verdade de mensagem (cursor de paginação, contagem de não
         * lidas, watcher de nova mensagem, busca, mapas por id). Uma anotação lá
         * dentro corrompe todos eles. O merge é só de exibição e acontece no
         * último passo, já depois do agrupamento de mídia.
         *
         * Recorte: só anotações dentro da janela de mensagens já carregada. Sem
         * isso, uma nota de meses atrás abriria um grupo de dia sozinha, sem
         * nenhuma conversa em volta. Ao rolar para trás, mais notas aparecem.
         *
         * O outro recorte é de permissão e vem pronto de `anotacoesInternasVisiveis`:
         * sem `inbox.acompanhar_atendimentos_agente` as linhas do Agente de IA não
         * chegam até aqui.
         *
         * As anotações vêm do chat ativo do `chatStore`, não de `props.messages`:
         * viewers (relatório de chat interno, histórico de atendimento) reutilizam
         * este componente com outras mensagens e herdariam as notas do último
         * atendimento aberto. Por isso só renderiza com `exibirAnotacoes`.
         */
        const anotacoesComoItens = computed<Message[]>(() => {
            if (!props.exibirAnotacoes || internalChatStore.isInternalChatMode) return [];

            const msgs = onlySignatureSquashedMessages.value;
            const limiteInferior = msgs.length ? new Date(msgs[0].time).getTime() : null;

            return chatStore.anotacoesInternasVisiveis
                .filter(a => limiteInferior === null || new Date(a.dataCriacao).getTime() >= limiteInferior)
                .map(a => chatStore.anotacaoToMessage(a));
        });

        /** Insere as anotações nos grupos de dia já montados, sem refazer o pipeline. */
        const injetarAnotacoes = (groups: { label: string; msgs: Message[] }[], notas: Message[]) => {
            if (notas.length === 0) return groups;

            const porLabel = new Map(groups.map(g => [g.label, g]));

            for (const nota of notas) {
                const label = getDateLabel(nota.time);
                const grupo = porLabel.get(label);

                if (grupo) {
                    grupo.msgs.push(nota);
                } else {
                    const novo = { label, msgs: [nota] };
                    porLabel.set(label, novo);
                    groups.push(novo);
                }
            }

            for (const grupo of groups) {
                grupo.msgs.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
            }

            return groups.sort(
                (a, b) => new Date(a.msgs[0].time).getTime() - new Date(b.msgs[0].time).getTime(),
            );
        };

        // Computada que agrupa as mensagens por Label de Data
        const groupedMessages = computed(() => {
            const groups: { label: string; msgs: Message[] }[] = [];
            const msgs = onlySignatureSquashedMessages.value;
            if (!msgs || msgs.length === 0) return injetarAnotacoes(groups, anotacoesComoItens.value);

            let currentGroup = {
                label: getDateLabel(msgs[0].time),
                msgs: [msgs[0]]
            };

            for (let i = 1; i < msgs.length; i++) {
                const msg = msgs[i];
                const label = getDateLabel(msg.time);

                if (label === currentGroup.label) {
                    currentGroup.msgs.push(msg);
                } else {
                    // Processa o grupo anterior com a lógica de mídia antes de fechar
                    currentGroup.msgs = groupConsecutiveMedia(currentGroup.msgs);
                    
                    groups.push(currentGroup);
                    currentGroup = { label, msgs: [msg] };
                }
            }
            // Aplica no último grupo também
            currentGroup.msgs = groupConsecutiveMedia(currentGroup.msgs);
            groups.push(currentGroup);

            return injetarAnotacoes(groups, anotacoesComoItens.value);
        });

        // Anotações chegam DEPOIS das mensagens (e por realtime), aumentando a
        // conversa sem mexer em `props.messages.length` — quem estava no fim
        // ficava 100-350px acima dele. Só reposiciona quem já estava no fim.
        watch(() => anotacoesComoItens.value.length, async (n, o) => {
            if (n <= o || !isUserAtBottom.value) return;
            await nextTick();
            scrollToBottomSmoothLocal();
        });

        const initializedAt = Date.now();
        const chatConversation = ref<HTMLDivElement|null>(null);

        const isWaitingForScroll = ref(false);
        const isScrolling = ref(false);

        async function waitForScrolling() {
            isWaitingForScroll.value = true;
            await waitUntil(() => isScrolling.value == false, 100, 10000); 
            isWaitingForScroll.value = false;
            return true;
        }

        function setScrollingFlag() {
            isScrolling.value = true;
            return () => isScrolling.value = false;
        }

        async function fetchNextPage() {
            // console.log('[fetchNextPage] can fetch more?',
            //     !isWaitingForScroll.value && ((Date.now() > (initializedAt + 2 * 1000)) && ((document.querySelector('.chat-conversation')?.scrollTop ?? 300) < 300) && !isFetchingMoreMessages.value && !noMoreMessages.value),
            //     {
            //         horaInicio: (initializedAt + 2 * 1000),
            //         horaAgora: Date.now(),
            //         passaramDoisSegundos: (Date.now() > (initializedAt + 2 * 1000)),
            //         trecoTaPertoToTopo: ((document.querySelector('.chat-conversation')?.scrollTop ?? 300) < 300),
            //         estaPodendoBuscarMais: !isFetchingMoreMessages.value,
            //         temMaisMsg: !noMoreMessages.value,
            //     });
            if (isWaitingForScroll.value) return;
            else await waitForScrolling();

            // Durante a abertura o conteúdo nem está na tela (spinner, ou páginas da
            // primeira não lida chegando pelo `selectChat`): buscar aqui duplicaria a busca.
            if (isLoadingMessages.value || aberturaPendente.value) return;

            const chatDiv = chatConversation.value;
            if (!chatDiv) return;

            if ((Date.now() > (initializedAt + 2 * 1000)) && (chatDiv.scrollTop < 450) && !isFetchingMoreMessages.value && !noMoreMessages.value) {
                isFetchingMoreMessages.value = true;

                // Segura a mensagem que está na tela, não a altura total: a conta antiga
                // (`scrollTop = alturaNova - alturaAntiga`) jogava fora o quanto o usuário
                // já estava abaixo do topo — até 450px de salto — e somava em dobro com a
                // ancoragem nativa do navegador quando ela também agia.
                const referencia = capturarReferencia();

                const { hasMore } = await chatStore.fetchNextPage();

                await nextTick();
                restaurarReferencia(referencia);

                if (!hasMore)
                    noMoreMessages.value = true;

                isFetchingMoreMessages.value = false;
            }
        }

        watch(() => chatStore.activeChatId, () => noMoreMessages.value = false);

        const isFetchingMoreMessages = ref(false);
        const noMoreMessages = ref(false);
        const previousScrollHeight = ref(0);

        watch(
            () => internalChatStore.messages.length,
            async (newVal, oldVal) => {
                if (!previousScrollHeight.value || newVal <= oldVal) return;

                await nextTick();

                const el = document.querySelector(
                    '.chat-conversation'
                ) as HTMLDivElement | null;

                if (!el) return;

                const newScrollHeight = el.scrollHeight;
                const diff = newScrollHeight - previousScrollHeight.value;

                el.scrollTop = diff;

                previousScrollHeight.value = 0;
            },
            { flush: 'post' }
        );

        // ==============================================
        // Abertura na primeira não lida
        // ==============================================

        const itensExibidos = computed(() => groupedMessages.value.flatMap(g => g.msgs));

        const itemDivisorAbertura = computed(() => {
            if (!props.exibirNaoLidas || currentStore.value.initialUnreadCount <= 0) return null;
            return itemComPrimeiraNaoLida(itensExibidos.value, idsNaoLidasNaAbertura.value);
        });

        // Mensagens que chegaram enquanto o usuário lia o histórico. Com o separador da
        // abertura na tela elas já estão abaixo dele: um segundo separador só faria ruído.
        const itemDivisorNovas = computed(() => {
            if (itemDivisorAbertura.value || !tempUnreadMessageId.value) return null;
            return itemComPrimeiraNaoLida(itensExibidos.value, new Set([tempUnreadMessageId.value]));
        });

        /** "No fim" (tolerância de 200px) liga a rolagem automática; longe dele, aparece o botão de voltar. */
        function atualizarEstadoDeRolagem() {
            const el = chatConversation.value;
            if (!el) return;
            // Seguindo o fim, nossas rolagens só descem: subir é sempre o usuário, mesmo
            // por caminhos que não passam pelos gestos de `liberarAncora` (busca do
            // navegador, leitor de tela).
            if (ancora === 'fim' && el.scrollTop < ultimoScrollTop - 1) ancora = null;
            ultimoScrollTop = el.scrollTop;
            const distanceFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
            isUserAtBottom.value = distanceFromBottom < 200;
            showScrollBottomBtn.value = distanceFromBottom > 200;
        }

        function rolarParaOFim() {
            const el = chatConversation.value;
            if (el) el.scrollTop = el.scrollHeight;
        }

        /**
         * Põe o separador da abertura a `MARGEM_TOPO_DIVISOR` do topo. O navegador
         * limita ao fim do conteúdo: com poucas não lidas a conversa fica no fim, e o
         * separador aparece mais abaixo, com todas elas visíveis.
         */
        function alinharNoDivisor(): boolean {
            const el = chatConversation.value;
            const divisor = el?.querySelector('[data-divisor-abertura]');
            if (!el || !divisor) return false;
            const desvio = divisor.getBoundingClientRect().top - el.getBoundingClientRect().top - MARGEM_TOPO_DIVISOR;
            if (Math.abs(desvio) >= 1) el.scrollTop += desvio;
            return true;
        }

        function capturarReferencia(): ReferenciaVisivel | null {
            const el = chatConversation.value;
            if (!el) return null;
            const topo = el.getBoundingClientRect().top;
            const itens = el.querySelectorAll('[data-message-id]');
            // Busca binária: os itens estão em ordem visual, e ler centenas de
            // retângulos a cada evento de scroll pesaria.
            let inicio = 0;
            let fim = itens.length - 1;
            let achado: Element | null = null;
            while (inicio <= fim) {
                const meio = (inicio + fim) >> 1;
                if (itens[meio].getBoundingClientRect().bottom > topo) {
                    achado = itens[meio];
                    fim = meio - 1;
                } else {
                    inicio = meio + 1;
                }
            }
            const id = achado?.getAttribute('data-message-id');
            return achado && id ? { id, topo: achado.getBoundingClientRect().top - topo } : null;
        }

        /** Devolve à referência a distância que ela tinha do topo. Zero quando a ancoragem nativa já agiu. */
        function restaurarReferencia(referencia: ReferenciaVisivel | null) {
            const el = chatConversation.value;
            if (!el || !referencia) return;
            const item = el.querySelector(`[data-message-id="${CSS.escape(referencia.id)}"]`);
            if (!item) return;
            const desvio = item.getBoundingClientRect().top - el.getBoundingClientRect().top - referencia.topo;
            if (Math.abs(desvio) >= 1) el.scrollTop += desvio;
        }

        /** Chamada a cada scroll: sem ancoragem nativa, guarda o que o usuário está vendo. */
        function registrarPosicaoDeLeitura() {
            if (!ancoragemNativa) referenciaLeitura = capturarReferencia();
        }

        /** Gesto do usuário (roda, toque, clique, tecla): a posição passa a ser dele. */
        function liberarAncora() {
            ancora = null;
        }

        /**
         * Toda mudança de altura do conteúdo ou do container passa por aqui, nesta
         * ordem de prioridade:
         *  1. abertura com não lidas: o separador continua onde foi posto, mesmo com
         *     mídia acima terminando de carregar;
         *  2. seguindo a última mensagem: o deslize continua até o novo fim;
         *  3. quem está no fim continua no fim (mensagem nova, "escrevendo...");
         *  4. quem lê o histórico fica parado — no Safari, pela referência guardada.
         */
        function aoRedimensionarConteudo() {
            if (ancora === 'divisor') {
                if (alinharNoDivisor()) return;
                // O separador sumiu (o operador respondeu e `initialUnreadCount` zerou).
                ancora = null;
            }
            if (ancora === 'fim') {
                scrollToBottomSmoothLocal();
                return;
            }
            if (isUserAtBottom.value) {
                rolarParaOFim();
                return;
            }
            if (!ancoragemNativa) {
                restaurarReferencia(referenciaLeitura);
                referenciaLeitura = capturarReferencia();
            }
        }

        /**
         * Põe a conversa recém-carregada no separador de não lidas. Roda depois do
         * render e antes do navegador pintar — o usuário nunca a vê no lugar errado.
         * Sem não lidas devolve 'fim', e quem chamou faz a abertura de sempre
         * (`scrollToBottomAnimated`). `null`: trocou de conversa no meio, a abertura
         * da nova cuida de si.
         */
        async function posicionarAbertura(): Promise<'divisor' | 'fim' | null> {
            aberturaPendente.value = false;

            const chatId = currentStore.value.activeChatId;
            idsNaoLidasNaAbertura.value = idsNaoLidas(props.messages, currentStore.value.initialUnreadCount);
            await nextTick(); // separador desenhado

            if (currentStore.value.activeChatId !== chatId || aberturaPendente.value) return null;

            ancora = alinharNoDivisor() ? 'divisor' : 'fim';
            // Sem esperar o evento de scroll: se a posição não mudou, ele nem dispara.
            atualizarEstadoDeRolagem();
            registrarPosicaoDeLeitura();
            // A animação de abertura começa saltando para 300px antes do fim, que pode
            // ficar ACIMA de onde a conversa anterior estava: não é o usuário subindo.
            ultimoScrollTop = 0;
            return ancora;
        }

        const shouldShowDate = (msg: Message, index: number, messageArray: Message[]) => {
            const aWeekAgo        = new Date(Date.now() - (7 * 24 * 3600 * 1000));
            const yesterdayDate   = new Date(Date.now() - (24 * 3600 * 1000));
            const todayDate       = new Date();
            const thisMessageDate = new Date(msg.time);

            const isSameForDayMonthYear = (A: Date, B: Date) => A.toISOString().slice(0, 10) == B.toISOString().slice(0,10);
            const capitalize1st         = (input: string) => `${input.at(0)?.toUpperCase() ?? ''}${input.slice(1, Infinity) ?? ''}`;

            const isThisFromToday     = isSameForDayMonthYear(thisMessageDate, todayDate);
            const isThisFromYesterday = isSameForDayMonthYear(thisMessageDate, yesterdayDate);
            const isThisLessThan7DaysOld  = thisMessageDate > aWeekAgo;
            const isThisFromThisYear  = thisMessageDate.getFullYear() == new Date().getFullYear();
            
            const dayOfTheMonth      = thisMessageDate.getDate();
            const longWeekdayMessage = thisMessageDate.toLocaleString('pt-BR',{ weekday:'long' })
            const longMonthMessage   = thisMessageDate.toLocaleString('pt-BR',{ month:'long' })

            const getDate = () => {
                return isThisFromToday      ? 'Hoje'
                    : isThisFromYesterday   ? 'Ontem'
                    : isThisLessThan7DaysOld   ? `${capitalize1st(longWeekdayMessage)}`
                    : isThisFromThisYear    ?  `${dayOfTheMonth} de ${longMonthMessage}`
                    : `${dayOfTheMonth} de ${longMonthMessage} de ${thisMessageDate.getFullYear()}`
            }
            if (index == 0) return getDate();

            // @ts-expect-error sim
            msg._times = {thisMessageDate,todayDate, yesterdayDate, aWeekAgo, isThisFromToday,isThisFromYesterday,isThisLessThan7DaysOld,isThisFromThisYear,};
            
            const prevMessageDate = new Date(messageArray[index - 1].time);
            if (isSameForDayMonthYear(thisMessageDate, prevMessageDate)) return false;
            else return getDate();
        }

        const hasWallpaperImage = computed(() => !!branding.images.chatWallpaper);

        const wallpaperImageVar = computed(() => {
            return branding.images.chatWallpaper ? `url('${branding.images.chatWallpaper}')` : 'none';
        });

        return {
            hasWallpaperImage,
            wallpaperImageVar,
            noMoreMessages,
            isFetchingMoreMessages,
            waitForScrolling,
            setScrollingPromise: setScrollingFlag,
            fetchNextPage,
            groupedMessages,
            shouldShowDate,
            isLoadingMessages,
            isForwarding: computed(() => currentStore.value.isForwarding),
            forwardingMessageIds: computed(() => currentStore.value.forwardingMessageIds),
            pinnedMessageId: computed(() => chatStore.activeChat?.pinnedMessageId || null),
            chatStore,
            uiStore,
            internalChatStore,
            previousScrollHeight,
            currentStore,
            itemDivisorAbertura,
            itemDivisorNovas,
            rotuloNaoLidas,
            initialUnreadCount: computed(() => currentStore.value.initialUnreadCount),
            chatConversation,
            chatContainerRef,
            resizeObserver,
            isUserAtBottom,
            showScrollBottomBtn,
            newMessagesCount,
            tempUnreadMessageId,
            presencaDigitando,
            scrollToBottomSmoothLocal,
            atualizarEstadoDeRolagem,
            registrarPosicaoDeLeitura,
            liberarAncora,
            aoRedimensionarConteudo,
            aberturaPendente,
            posicionarAbertura,
        }
    },
    props: {
        messages: {
            type: Array as PropType<Message[]>,
            default: () => []
        },
        highlightQuery: {
            type: String,
            default: ''
        },

        isSidebarOpen: { 
            type: Boolean, 
            default: false 
        },

        exibirAnotacoes: {
            type: Boolean,
            default: false
        },

        /**
         * Separador de não lidas e abertura posicionada nele. Mesmo motivo de
         * `exibirAnotacoes`: o contador vem da conversa ativa da store, e os viewers
         * (histórico de atendimento, relatório de chat interno) reutilizam este
         * componente com outras mensagens — herdariam um separador sem sentido.
         */
        exibirNaoLidas: {
            type: Boolean,
            default: false
        }
    },
    emits: [ 'delete-message', 'edit-message', 'reply-message', 'react-message', 'forward-message', 'copy-message', 'star-message', 'pin-message' ],
    data() {
        return {
            unwatchScrollTo: null as null|(() => void),

            isWaitingForScroll: false as boolean,
            scrollingPromise  : Promise.resolve() as Promise<void>,
        }
    },
    mounted() {
        this.unwatchScrollTo = this.uiStore.$subscribe((mutation, state) => {
            if (state.messageIdToScrollTo) {
                this.scrollToMessage(state.messageIdToScrollTo);
            }
        });

        const el = this.$refs.chatConversation as HTMLDivElement;
        const conteudo = this.$refs.conteudoConversa as HTMLDivElement | undefined;
    
        // O container muda com a janela e com o composer; o invólucro, com tudo que
        // rola. Antes o segundo alvo era `el.firstElementChild` capturado aqui — o
        // spinner ou o primeiro dia, que somem no primeiro recarregamento.
        this.resizeObserver = new ResizeObserver(() => this.aoRedimensionarConteudo());

        if (el) this.resizeObserver.observe(el);
        if (conteudo) this.resizeObserver.observe(conteudo);

        if (!this.exibirNaoLidas) {
            // Viewers: abertura de sempre.
            this.scrollToBottomInstant();
            setTimeout(() => this.scrollToBottomSmooth(), 100);
            return;
        }

        // Conversa ativa: abre quando termina de carregar (ou já, se montou com ela carregada).
        this.$watch(() => !this.isLoadingMessages && this.aberturaPendente, (pronta: boolean) => {
            if (pronta) void this.abrirConversa();
        }, { flush: 'post' });
        if (!this.isLoadingMessages) void this.abrirConversa();
    },
    beforeUnmount() {
        if (this.unwatchScrollTo) this.unwatchScrollTo();
        
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
    methods: {
        /**
         * Com não lidas, no separador. Sem elas, a abertura de sempre: a mesma
         * animação que o AreaChatUser disparava, deslizando até a última mensagem.
         */
        async abrirConversa() {
            if (await this.posicionarAbertura() === 'fim') this.scrollToBottomAnimated();
        },

        handleScroll() {
            debounce(this.fetchNextPage, 100, 'getNextPage');

            const el = this.$refs.chatConversation as HTMLDivElement | undefined;
            if (!el) return;

            const { scrollTop, scrollHeight } = el;

            this.atualizarEstadoDeRolagem();
            this.registrarPosicaoDeLeitura();
            
            // if (this.isUserAtBottom) {
            //     if (this.newMessagesCount > 0) {
            //         this.newMessagesCount = 0;
            //     }
            //     if (this.chatStore.activeChat && this.chatStore.activeChat.unreadCount > 0) {
            //         this.chatStore.resolveMarkChatAsRead(this.chatStore.activeChatId as any);
            //     }
            // }

            if (scrollTop === 0 && this.internalChatStore.nextCursor && !this.internalChatStore.isLoadingMessages) {
                this.previousScrollHeight = scrollHeight;
                this.internalChatStore.loadMessages(this.internalChatStore.nextCursor);
            }
        },

        handleScrollToBottomClick() {
            this.scrollToBottomSmooth();
            // this.newMessagesCount = 0;
        },

        async scrollToBottomSmooth() {
            const onFinishedScrolling = this.setScrollingPromise();
            const el = this.$refs.chatConversation as HTMLDivElement|undefined;
            if (el) {
                el.scrollTo({
                    top: el.scrollHeight,
                    behavior: 'smooth'
                });
                await delay(50);
                onFinishedScrolling();
            } else {
                onFinishedScrolling();
            }
        },

        scrollToBottom() {
            const onFinishedScrolling = this.setScrollingPromise();
            this.$nextTick(() => {
                const el = this.$refs.chatConversation as HTMLDivElement|undefined;
                if (!el) return onFinishedScrolling();

                requestAnimationFrame(() => {
                    const currentHeight = el.scrollHeight;
                    const clientHeight = el.clientHeight;

                    const jumpPosition = Math.max(0, currentHeight - clientHeight - 600);
                    
                    el.scrollTop = jumpPosition;

                    setTimeout(async () => {
                        el.scrollTo({
                            top: el.scrollHeight, 
                            behavior: 'smooth'
                        });
                        await delay(50);
                        onFinishedScrolling();
                    }, 150); 
                });
            });
        },

        scrollToBottomAnimated() {
            const onFinishedScrolling = this.setScrollingPromise();
            this.$nextTick(() => {
                const el = this.$refs.chatConversation as HTMLDivElement | undefined;
                if (!el) return onFinishedScrolling();

                const scrollHeight = el.scrollHeight;
                const clientHeight = el.clientHeight;
                const maxScroll = scrollHeight - clientHeight;

                if (maxScroll <= 0) return onFinishedScrolling();

                const startPosition = Math.max(0, maxScroll - 300);
                
                el.scrollTop = startPosition;

                requestAnimationFrame(async () => {
                    el.scrollTo({
                        top: scrollHeight,
                        behavior: 'smooth'
                    });
                    await delay(50);
                    onFinishedScrolling()
                });
            });
        },

        async scrollToBottomInstant() {
            const onFinishedScrolling = this.setScrollingPromise();
            const el = this.$refs.chatConversation as HTMLDivElement | undefined;
            if (el) {
                el.scrollTop = el.scrollHeight;
                await delay(50);
                onFinishedScrolling()
            } else onFinishedScrolling()
        },

        handleDelete(msgId: string) { this.$emit('delete-message', msgId); },
        handleEdit(msg: Message) { this.$emit('edit-message', msg); },
        handleReply(msg: Message) { this.$emit('reply-message', msg); },
        handleReact(msg: Message, emoji: string) { this.$emit('react-message', msg, emoji); },
        handleForward(msg: Message) { this.$emit('forward-message', msg); },
        handleCopy(msg: Message) { this.$emit('copy-message', msg); },
        handleStar(msg: Message) { this.$emit('star-message', msg); },
        handlePin(msg: Message) { this.$emit('pin-message', msg); },
        handleToggleForward(messageId: string) { (this.currentStore as any).toggleForwardMessage(messageId); },

        scrollToMessage(messageId: string) {
            // Busca, citação, mensagem fixada: o usuário pediu outro lugar.
            this.liberarAncora();
            this.$nextTick(() => {
                const el = this.$refs.chatConversation as HTMLDivElement|undefined;
                if (!el || !this.uiStore) return;

                const messageElement = el.querySelector(`[data-message-id="${messageId}"]`);
                
                if (messageElement) {
                    messageElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center' 
                    });
                    messageElement.classList.add('highlight-message');
                    setTimeout(() => {
                        messageElement.classList.remove('highlight-message');
                    }, 400);
                    this.uiStore.clearScrollTo();
                } else {
                    this.uiStore.clearScrollTo();
                }
            });
        }
    }
})
</script>

<style scoped>
/* Balão de "escrevendo..." — segue as MESMAS regras da bolha recebida de
   ChatMessage.vue (`li.msg_wpp_render.received-message .ctext-wrap-content`):
   canto inferior esquerdo reto (12px 12px 12px 0), branco no claro e
   `--bs-light` no escuro. Classes próprias porque reaproveitar as do ChatMessage
   acoplaria os componentes; os VALORES são deliberadamente os mesmos. */
/* Em grupo, cada mensagem recebida é precedida por `.group-chat-avatar-container`
   (32px + 8px de margem, ChatMessage.vue). Sem este recuo o balão de digitação
   ficava 40px à esquerda de todas as bolhas acima, criando um degrau visível. */
.presenca-bubble-row.presenca-em-grupo {
    padding-left: 40px;
}

.presenca-bubble-row {
    display: flex;
    justify-content: flex-start;
    width: 100%;
    margin-bottom: 16px;
    padding-right: 20px;
}

.presenca-bubble {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    max-width: 550px;
    padding: 10px;
    border: none;
    border-radius: 12px 12px 12px 0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

[data-bs-theme="light"] .presenca-bubble {
    background: var(--bs-white, #fff);
    color: var(--bs-body-color);
}

[data-bs-theme="dark"] .presenca-bubble {
    background: var(--bs-light);
    color: var(--bs-dark);
}

/* Espelha `.group-sender-name`: 13px/700, com a cor pastel do autor vindo inline. */
.presenca-autor {
    font-size: 13px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 180px;
}

.presenca-dots {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.presenca-dots i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: var(--bs-text-muted);
    opacity: .35;
    animation: presenca-pulse 1.2s infinite ease-in-out;
}

.presenca-dots i:nth-child(2) { animation-delay: .2s; }
.presenca-dots i:nth-child(3) { animation-delay: .4s; }

@keyframes presenca-pulse {
    0%, 60%, 100% { opacity: .35; transform: translateY(0); }
    30%           { opacity: 1;   transform: translateY(-3px); }
}

/* Gravação de áudio. O vermelho + `ri-mic-fill` vêm do `AudioRecorder.vue`, que os usa no
   rec-dot, no texto "Gravando..." e nos botões — NÃO nas barras: as dele são desenhadas no
   canvas com `branding.colors.light.success` (verde), e são visualizador de amplitude real,
   não um placeholder animado. Ele escreve `#ef476f` literal, mas esse valor É `--bs-danger`
   no tema; aqui usa-se o token, como as regras vizinhas de presença (`--bs-text-muted`,
   `--bs-success`) e como a maioria do repo. Mesma cor nos dois temas — `--bs-danger` não é
   redefinido em `[data-bs-theme="dark"]`. */
.presenca-mic {
    font-size: 14px;
    line-height: 1;
    color: var(--bs-danger);
}

.presenca-waves {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    height: 14px;
}

.presenca-waves i {
    width: 2px;
    height: 100%;
    border-radius: 1px;
    background-color: var(--bs-danger);
    opacity: .55;
    transform-origin: center;
    animation: presenca-wave .9s infinite ease-in-out;
}

.presenca-waves i:nth-child(2) { animation-delay: .15s; }
.presenca-waves i:nth-child(3) { animation-delay: .30s; }
.presenca-waves i:nth-child(4) { animation-delay: .45s; }

@keyframes presenca-wave {
    0%, 100% { transform: scaleY(.35); opacity: .55; }
    50%      { transform: scaleY(1);   opacity: 1; }
}

/* Respeita quem pediu menos movimento no sistema: mantém os pontinhos e as barras
   visíveis, sem animar — o sinal continua legível, só que parado. */
@media (prefers-reduced-motion: reduce) {
    .presenca-dots i { animation: none; opacity: .6; }
    /* Parada, a barra perde o pico de opacidade da animação — some no tema claro.
       Vai a opacidade cheia; a diferença de altura já distingue. */
    .presenca-waves i { animation: none; transform: scaleY(.6); opacity: 1; }
}

/* CSS Existente */
.chat-conversation {
    flex: 1 1 0;
    overflow-y: auto;
    position: relative !important;
    background-repeat: repeat;
    background-position: center;
    background-size: 500px auto;
    background-attachment: fixed;
    /* scroll-behavior: smooth;  */
}
.chat-conversation.has-wallpaper-image {
    background-image: var(--chat-wallpaper-image, none);
}
.chat-conversation:not(.has-wallpaper-image) {
    background-image: radial-gradient(circle, rgba(var(--bs-primary-rgb), 0.05) 1.5px, transparent 1.5px);
    background-size: 24px 24px;
}
.chat-conversation::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
}
.list-unstyled {
    position: relative;
    z-index: 1;
}
.chat-conversation {
    scrollbar-width: thin;
    scrollbar-color: var(--bs-light) transparent;
}
.chat-conversation::-webkit-scrollbar {
    width: 8px;
}
.chat-conversation::-webkit-scrollbar-track {
    background: transparent;
}
.chat-conversation::-webkit-scrollbar-thumb {
    background-color: var(--bs-light);
    border-radius: 10px;
    border: 2px solid var(--bs-white);
}
.chat-day-title-container {
    position: -webkit-sticky; 
    position: sticky;
    top: 10px; 
    z-index: 50; 
    text-align: center;
    margin-bottom: 24px;
    margin-top: 12px;
    pointer-events: none;
    clear: both; 
}
.chat-day-title .title {
    background-color: var(--bs-badge-soft-bg, var(--bs-light));
    color: var(--bs-text-muted);
    position: relative;
    font-size: 13px;
    font-weight: 500;
    z-index: 1;
    padding: 6px 12px;
    border-radius: 6px;
    
    box-shadow: 0 1px 2px rgba(0,0,0,0.15); 
    display: inline-block;
    pointer-events: auto; 
}

[data-bs-theme="dark"] .chat-day-title .title {
    background-color: #202c33; /* Cor aproximada do header de data do Wpp Dark */
    color: #8696a0;
    border: 1px solid rgba(255,255,255,0.05);
}

:deep(li.highlight-message) {
    background-color: #6ae6c709 !important;
    transition: background-color 0.3s ease-in-out;
    border-radius: 8px;
}

/* Botão Flutuante */
.btn-scroll-bottom {
    position: fixed; 
    bottom: 80px;
    right: 9px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: var(--bs-secondary-bg); 
    color: var(--bs-secondary-color);
    border: 1px solid var(--bs-border-color);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 99;
    font-size: 20px;
    transition: right 0.3s cubic-bezier(0.25, 0.8, 0.5, 1), transform 0.2s, background-color 0.2s;
}

.btn-scroll-bottom.sidebar-open {
    right: 457px; 
}

@media (max-width: 992px) {
    .btn-scroll-bottom.sidebar-open {
        right: 20px;
        z-index: 1; /* Esconde atrás da sidebar no mobile */
    }
}

.btn-scroll-bottom:hover {
    background-color: var(--bs-success);
    color: #fff;
    transform: translateY(-3px);
}

.btn-scroll-bottom.has-new-messages {
    background-color: var(--bs-success);
    color: #fff;
    border-color: var(--bs-success);
}
.btn-scroll-bottom.has-new-messages:hover {
    background-color: var(--brand-success-hover);
}

.scroll-badge {
    position: absolute;
    top: -5px;
    left: -5px;
    background-color: #00a884; /* Verde WhatsApp */
    color: white;
    font-size: 11px;
    font-weight: 700;
    min-width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid var(--bs-body-bg);
    z-index: 100;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

[data-bs-theme="dark"] .btn-scroll-bottom {
    background-color: #2a3942;
    color: #aebac1;
    border-color: rgba(255,255,255,0.1);
}
[data-bs-theme="dark"] .btn-scroll-bottom:hover {
    background-color: var(--bs-success);
    color: #fff;
}

/* Separador "N mensagens não lidas": faixa de largura total com a pílula no
   centro, como no WhatsApp Web. A pílula tem a geometria do chip de data (13px,
   raio 6px, mesmo padding) e a sombra das bolhas, para ler como parte da mesma
   família; o que a distingue é o acento da marca, na faixa e no texto. */
.unread-divider-container {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 100%;
    margin: 16px 0;
    padding: 6px 0;
}

.unread-divider-line {
    position: absolute;
    inset: 0;
    border-radius: 6px;
    background-color: rgba(var(--bs-success-rgb), 0.08);
}

.unread-divider-badge {
    position: relative;
    z-index: 1;
    max-width: 100%;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    text-align: center;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* `--brand-success` puro sobre branco fica abaixo de 3:1 de contraste. O tom
   escuro da marca, escurecido mais 20%, fica acima de 6:1 em todas as marcas. */
[data-bs-theme="light"] .unread-divider-badge {
    background-color: var(--bs-white, #fff);
    color: color-mix(in srgb, var(--brand-success-dark, #048a67) 80%, #000);
}

/* No escuro o verde da marca já contrasta (≥ 4.7:1) com a superfície secundária. */
[data-bs-theme="dark"] .unread-divider-badge {
    background-color: var(--bs-secondary-bg);
    color: var(--bs-success);
    border: 1px solid rgba(255, 255, 255, 0.05);
}

[data-bs-theme="dark"] .unread-divider-line {
    background-color: rgba(var(--bs-success-rgb), 0.1);
} 
</style>